import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RootState } from "../store";
import { Order } from "../types/order";

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return headers;
  },
});

export const orderSlice = createApi({
  reducerPath: "orderSlice",
  baseQuery,
  tagTypes: ["Orders", "Customers"],
  // keep data cached for 5 minutes to avoid refetching when navigating
  keepUnusedDataFor: 300,
  refetchOnMountOrArgChange: 30, // only refetch if older than 30 seconds
  refetchOnFocus: false, // don't refetch just because window gets focus
  refetchOnReconnect: true, // do refetch when reconnecting
  endpoints: (builder) => ({
    getOrders: builder.query<Order[], void>({
      query: () => "/orders",
      providesTags: ["Orders", "Customers"],
    }),
    getAllOrders: builder.query<Order[], void>({
      query: () => "/orders/all",
      providesTags: ["Orders", "Customers"],
    }),
    getOrderById: builder.query<Order, string>({
      query: (id) => `/orders/${id}`,
      providesTags: ["Orders"],
    }),
    deleteOrder: builder.mutation<void, string>({
      query: (id) => ({
        url: `/orders/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Orders"],
    }),
    createOrder: builder.mutation<Order, FormData>({
      query: (formData) => ({
        url: "/orders",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Orders"],
    }),
    updateOrder: builder.mutation<Order, { id: string; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `/orders/${id}`,
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: ["Orders"],
    }),
    updateOrderStatus: builder.mutation<Order, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/orders/${id}/status`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: ["Orders"],
    }),
    completeOrderWithPhoto: builder.mutation<
      Order,
      {
        id: string;
        photoUrl: string;
        fileName?: string;
        mimeType?: string;
        fileSize?: number;
      }
    >({
      query: ({ id, photoUrl, fileName, mimeType, fileSize }) => ({
        url: `/orders/${id}/complete-with-photo`,
        method: "PUT",
        body: { photoUrl, fileName, mimeType, fileSize },
      }),
      invalidatesTags: ["Orders"],
    }),
  }),
});

export const {
  useGetAllOrdersQuery,
  useGetOrdersQuery,
  useGetOrderByIdQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
  useUpdateOrderStatusMutation,
  useDeleteOrderMutation,
  useCompleteOrderWithPhotoMutation,
  // export prefetch methods for manual prefetching
  usePrefetch,
} = orderSlice;
